"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Paper,
  Button,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
// import { getMembershipDetails } from "../apis/membershipApi"
import { useAuth } from "../context/AuthContext";
import { getMembershipById } from "../apis/membershipApi";
import { trackSpotifyPurchase } from "../utils/loadSpotifyAds";

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { userId } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [membershipConfirmed, setMembershipConfirmed] = useState(false);

  useEffect(() => {
    if (!sessionId || !userId) return;

    let cancelled = false;
    const reportPurchase = async () => {
      let value: number | undefined;
      if (userId) {
        try {
          const response = await getMembershipById(userId);
          const memberships = Array.isArray(response?.membership)
            ? response.membership
            : [];
          const latest = memberships[memberships.length - 1];
          const amount = Number(latest?.amount);
          if (Number.isFinite(amount)) value = amount;
        } catch {
          // Stripe already redirected here; still report the conversion.
        }
      }
      if (!cancelled) {
        trackSpotifyPurchase({ value, orderId: sessionId });
      }
    };

    void reportPurchase();
    return () => {
      cancelled = true;
    };
  }, [sessionId, userId]);

  // Poll for membership status
    useEffect(() => {
      if (!userId) return

      const checkMembershipStatus = async () => {
        setIsLoading(true);
        try {
          const response = await getMembershipById(userId)
          if (response && response.data) {
            setMembershipConfirmed(true)
            setIsLoading(false)
          }
        } catch (error) {
          // If membership not found yet, we'll try again
          console.log("Membership not confirmed yet, retrying...")
        } finally {
          setIsLoading(false);
        }
      }

      // Check immediately
      checkMembershipStatus()
    }, [userId, membershipConfirmed])

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="h6">Processing your membership...</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments. Please don't close this page.
            </Typography>
          </Box>
        ) : membershipConfirmed ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 80, color: "success.main" }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Membership Activated!
            </Typography>
            <Typography variant="body1" paragraph>
              Thank you for your purchase. Your membership has been successfully
              activated.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Session ID: {sessionId}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" color="primary" href="/dashboard">
                Go to Dashboard
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography variant="h5" component="h1" gutterBottom>
              Payment Received
            </Typography>
            <Typography variant="body1" paragraph>
              Your payment has been processed, but we're still activating your
              membership.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This can take a few minutes. You can check your membership status
              in your account dashboard.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" color="primary" href="/dashboard">
                Go to Dashboard
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default MembershipSuccess;
